import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import {
  getScanConfig,
  updateScanConfig,
} from "@/lib/security-config.functions";
import {
  RULE_CATALOG,
  effectiveThresholds,
  type CategoriesEnabled,
  type RuleConfig,
  type ScanConfig,
  type Sensitivity,
} from "@/lib/security/config";
import { AdminSection } from "./admin";

export const Route = createFileRoute("/_authenticated/admin/security/settings")({
  head: () => ({
    meta: [
      { title: "Security settings · Admin · Buddha KA" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: SecuritySettings,
});

function SecuritySettings() {
  const fetchConfig = useServerFn(getScanConfig);
  const saveConfig = useServerFn(updateScanConfig);
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "security", "config"],
    queryFn: () => fetchConfig(),
    staleTime: 30_000,
  });

  const [draft, setDraft] = useState<ScanConfig | null>(null);
  const [banner, setBanner] = useState<string | null>(null);

  useEffect(() => {
    if (data) setDraft(structuredClone(data));
  }, [data]);

  const save = useMutation({
    mutationFn: async (input: ScanConfig) => saveConfig({ data: input }),
    onSuccess: () => {
      setBanner("Saved. New settings take effect on the next scan.");
      qc.invalidateQueries({ queryKey: ["admin", "security", "config"] });
      setTimeout(() => setBanner(null), 4000);
    },
    onError: (e: any) => setBanner(`Save failed: ${e?.message ?? "unknown error"}`),
  });

  if (isLoading || !draft) {
    return <p className="text-sm text-muted-foreground">Loading settings…</p>;
  }
  if (error) {
    return (
      <p className="text-sm text-red-400">
        Failed to load settings: {(error as Error).message}
      </p>
    );
  }

  function patch(next: Partial<ScanConfig>) {
    setDraft((d) => (d ? { ...d, ...next } : d));
  }
  function patchCategory(key: keyof CategoriesEnabled, value: boolean) {
    setDraft((d) =>
      d
        ? { ...d, categories_enabled: { ...d.categories_enabled, [key]: value } }
        : d,
    );
  }
  function patchRule(key: string, partial: Partial<RuleConfig>) {
    setDraft((d) =>
      d
        ? { ...d, rules: { ...d.rules, [key]: { ...d.rules[key], ...partial } } }
        : d,
    );
  }

  function reset() {
    if (data) setDraft(structuredClone(data));
  }

  return (
    <AdminSection
      title="Scanner settings"
      description="Tune scan window, category kill switches, and per-rule thresholds. Changes apply on the next scan run."
      action={
        <div className="flex gap-2">
          <button
            onClick={reset}
            disabled={save.isPending}
            className="rounded-full border hairline px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground disabled:opacity-50"
          >
            Reset
          </button>
          <button
            onClick={() => save.mutate(draft)}
            disabled={save.isPending}
            className="rounded-full bg-gold-gradient px-5 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-obsidian disabled:opacity-50"
          >
            {save.isPending ? "Saving…" : "Save changes"}
          </button>
        </div>
      }
    >
      {banner ? (
        <p className="mb-4 rounded-md border hairline bg-slate-900/40 px-3 py-2 text-xs text-muted-foreground">
          {banner}
        </p>
      ) : null}

      <div className="space-y-10">
        <section>
          <h3 className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
            Scan window
          </h3>
          <div className="mt-3 flex items-center gap-4">
            <input
              type="range"
              min={5}
              max={60}
              step={1}
              value={draft.default_window_minutes}
              onChange={(e) => patch({ default_window_minutes: Number(e.target.value) })}
              className="flex-1 accent-gold-luminous"
              aria-label="Default scan window in minutes"
            />
            <span className="w-24 text-right font-mono text-sm">
              {draft.default_window_minutes} min
            </span>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            Short-window rules (like failed sign-in bursts) count events across this
            interval. Long-window rules stay at 60 min.
          </p>
        </section>

        <section>
          <h3 className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
            Category kill switches
          </h3>
          <div className="mt-3 grid gap-3 sm:grid-cols-3">
            {(["auth", "permission", "push"] as const).map((c) => (
              <label
                key={c}
                className="flex cursor-pointer items-center justify-between rounded-lg border hairline px-4 py-3 text-sm"
              >
                <span className="capitalize">{c}</span>
                <input
                  type="checkbox"
                  checked={draft.categories_enabled[c]}
                  onChange={(e) => patchCategory(c, e.target.checked)}
                  className="h-4 w-4 accent-gold-luminous"
                />
              </label>
            ))}
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            Disabling a category stops every rule in it from firing, even if the rule
            itself is enabled below.
          </p>
        </section>

        <section>
          <h3 className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
            Rules
          </h3>
          <ul className="mt-3 space-y-3">
            {RULE_CATALOG.map((entry) => {
              const rule = draft.rules[entry.key];
              if (!rule) return null;
              const eff = effectiveThresholds(rule);
              const categoryOff = !draft.categories_enabled[entry.category];
              return (
                <li
                  key={entry.key}
                  className={`rounded-xl border hairline p-4 ${categoryOff ? "opacity-60" : ""}`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-serif text-lg">{entry.label}</p>
                      <p className="mt-1 font-mono text-[11px] text-muted-foreground">
                        {entry.key}
                      </p>
                      <p className="mt-2 max-w-md text-xs text-muted-foreground">
                        {entry.description}
                      </p>
                    </div>
                    <label className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em]">
                      <input
                        type="checkbox"
                        checked={rule.enabled}
                        onChange={(e) => patchRule(entry.key, { enabled: e.target.checked })}
                        className="h-4 w-4 accent-gold-luminous"
                      />
                      Enabled
                    </label>
                  </div>

                  <div className="mt-4 grid gap-4 sm:grid-cols-3">
                    <NumberField
                      label="Warn ≥"
                      value={rule.warn_threshold}
                      min={1}
                      max={10_000}
                      onChange={(v) => patchRule(entry.key, { warn_threshold: v })}
                    />
                    <NumberField
                      label="Critical ≥"
                      value={rule.critical_threshold}
                      min={1}
                      max={50_000}
                      onChange={(v) => patchRule(entry.key, { critical_threshold: v })}
                    />
                    <div>
                      <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                        Sensitivity
                      </p>
                      <div className="mt-2 inline-flex overflow-hidden rounded-full border hairline">
                        {(["low", "medium", "high"] as Sensitivity[]).map((s) => (
                          <button
                            key={s}
                            type="button"
                            onClick={() => patchRule(entry.key, { sensitivity: s })}
                            className={`px-3 py-1 text-xs capitalize transition ${
                              rule.sensitivity === s
                                ? "bg-gold-gradient font-semibold text-obsidian"
                                : "text-muted-foreground hover:text-foreground"
                            }`}
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  <p className="mt-3 text-[11px] text-muted-foreground">
                    Effective: warn at ≥{eff.warn}, critical at ≥{eff.critical} events per{" "}
                    {entry.window_scope === "short"
                      ? `${draft.default_window_minutes} min`
                      : "60 min"}
                    .
                    {categoryOff ? " · Category disabled — rule inactive." : ""}
                  </p>
                </li>
              );
            })}
          </ul>
        </section>
      </div>
    </AdminSection>
  );
}

function NumberField({
  label,
  value,
  min,
  max,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (v: number) => void;
}) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
        {label}
      </span>
      <input
        type="number"
        min={min}
        max={max}
        value={value}
        onChange={(e) => {
          const n = Number(e.target.value);
          if (Number.isFinite(n)) onChange(Math.max(min, Math.min(max, Math.round(n))));
        }}
        className="mt-1 w-full rounded-md border hairline bg-transparent px-3 py-1.5 font-mono text-sm"
      />
    </label>
  );
}
